import { createSlice } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'
import { LANGUAGE } from 'constants/layout'
import {
  FORM_EMAIL_KEY,
  FORM_ID_KEY,
  FORM_STEP_KEY,
  FORM_TYPE_KEY,
  PERSONAL_DATA_STEP_KEY,
} from 'constants/storage'
import i18n from 'translation/i18n'
import { FormTypes } from 'types/enums/EFormTypes'
import { EDirection, ELanguage } from 'types/enums/ELanguage'
import { EPersonalDetailsFormSteps } from 'types/enums/EPersonalDetailsFormSteps'
import { ESignupFormSteps } from 'types/enums/ESignupFormSteps'

export interface LayoutState {
  language: {
    code: ELanguage
    direction: EDirection
  }
  showNavbar: boolean
  showFooter: boolean
  formType: FormTypes
  formStep: ESignupFormSteps
  personalDetailsStep: EPersonalDetailsFormSteps
  isLoading: boolean
  authFormTexts: {
    title: string
    subtitle: string
  }
  user: {
    id: string
    email: string
  }
}

const formTypeFromSessionStorage = sessionStorage.getItem(
  FORM_TYPE_KEY,
) as FormTypes
const formStepFromSessionStorage = sessionStorage.getItem(
  FORM_STEP_KEY,
) as ESignupFormSteps
const personalDetailsStepFromSessionStorage = sessionStorage.getItem(
  PERSONAL_DATA_STEP_KEY,
) as EPersonalDetailsFormSteps

const rtlLanguages = [ELanguage.HEBREW]
const allowedCacheFormSteps = [
  ESignupFormSteps.verificationOption,
  ESignupFormSteps.personalDetails,
]

const storedLanguage = sessionStorage.getItem(LANGUAGE) as ELanguage | null
const languageCode = storedLanguage || ELanguage.HEBREW
const languageDirection = storedLanguage
  ? rtlLanguages.includes(storedLanguage)
    ? EDirection.RTL
    : EDirection.LTR
  : EDirection.RTL

const initialState: LayoutState = {
  language: {
    code: languageCode,
    direction: languageDirection,
  },
  showNavbar: false,
  showFooter: false,
  formType:
    formTypeFromSessionStorage === FormTypes.SUCCESS_PAGE
      ? FormTypes.LOGIN_FORM
      : formTypeFromSessionStorage || FormTypes.LOGIN_FORM,
  formStep:
    formTypeFromSessionStorage === FormTypes.SUCCESS_PAGE
      ? ESignupFormSteps.verificationOption
      : formStepFromSessionStorage || ESignupFormSteps.verificationOption,
  personalDetailsStep:
    formTypeFromSessionStorage === FormTypes.SUCCESS_PAGE
      ? EPersonalDetailsFormSteps.identityProvider
      : personalDetailsStepFromSessionStorage ||
        EPersonalDetailsFormSteps.identityProvider,
  isLoading: false,
  authFormTexts: {
    title: '',
    subtitle: '',
  },
  user: {
    id: sessionStorage.getItem(FORM_ID_KEY) || '',
    email: '',
  },
}

export const layoutSlice = createSlice({
  name: 'layout',
  initialState,
  reducers: {
    showNavbar: (state, action: PayloadAction<boolean>) => {
      state.showNavbar = action.payload
    },
    showFooter: (state, action: PayloadAction<boolean>) => {
      state.showFooter = action.payload
    },
    setLanguage: (state, action: PayloadAction<ELanguage>) => {
      const language = action.payload
      sessionStorage.setItem(LANGUAGE, language)
      i18n.changeLanguage(language)
      state.language.code = language
      state.language.direction = rtlLanguages.includes(language)
        ? EDirection.RTL
        : EDirection.LTR
    },
    setForm: (
      state,
      action: PayloadAction<{
        formType?: FormTypes
        formStep?: ESignupFormSteps
        personalDetailsStep?: EPersonalDetailsFormSteps
        id?: string
        email?: string
      }>,
    ) => {
      const { id, email, formType, formStep, personalDetailsStep } =
        action.payload
      if (id) {
        sessionStorage.setItem(FORM_ID_KEY, id)
        state.user.id = id
      }
      if (email) {
        sessionStorage.setItem(FORM_EMAIL_KEY, email)
        state.user.email = email
      }
      if (formType) {
        sessionStorage.setItem(FORM_TYPE_KEY, formType)
        state.formType = formType
      }
      if (formStep) {
        if (allowedCacheFormSteps.includes(formStep)) {
          sessionStorage.setItem(FORM_STEP_KEY, formStep)
        }
        state.formStep = formStep
      }
      if (personalDetailsStep) {
        sessionStorage.setItem(PERSONAL_DATA_STEP_KEY, personalDetailsStep)
        state.personalDetailsStep = personalDetailsStep
      }
      state.isLoading = false
    },
    setIsLayoutLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    setAuthFormTexts: (
      state,
      action: PayloadAction<{
        title: string
        subtitle: string
      }>,
    ) => {
      state.authFormTexts = action.payload
    },
  },
})

export const {
  showNavbar,
  showFooter,
  setLanguage,
  setForm,
  setAuthFormTexts,
  setIsLayoutLoading,
} = layoutSlice.actions

export default layoutSlice.reducer
